// ---------------------------------------------------------------------------
// Topbar — HotelStack (SOURCE OF TRUTH)
// ---------------------------------------------------------------------------
// SYNC NOTE: This component is the canonical version of the Topbar shared
// between HotelStack and BookMyRoom. Both apps use identical layout, search,
// property switcher, user menu, notification bell, and breadcrumb logic.
// The ONLY differences are default prop values:
//   1. `propertyName` default: 'HotelStack Grand Delhi' vs 'The Grand Regency'
//   2. `userName` default: 'Admin User' vs 'Guest User'
//   3. `userRole` default: 'Hotel Admin' vs 'Guest'
//   4. Property dropdown header: 'Switch to Hotel PMS' vs 'Select Property'
// When modifying topbar behaviour, apply the same change to both apps.
// See: apps/bookmyroom.io/shared/layout/Topbar.tsx
// ---------------------------------------------------------------------------
'use client';

import { useRef, useState, useEffect, useCallback } from 'react';
import { cn } from '@/core/utils/cn';
import {
  Search,
  Command,
  Bell,
  Building2,
  ChevronDown,
  User,
  Settings as SettingsIcon,
  LogOut,
  Menu,
  MapPin,
  ExternalLink,
} from 'lucide-react';
import { ThemeToggle } from '@/shared/ui/ThemeToggle';
import { Breadcrumb, type BreadcrumbItem } from './Breadcrumb';
import { LanguageSelector, useTranslation } from '@/core/i18n';
import { OfflineIndicator } from '@/shared/ui/OfflineIndicator';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export interface TopbarProperty {
  id: string;
  name: string;
  city: string;
  status?: string;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
interface TopbarProps {
  /** Text shown in the left breadcrumb area */
  title?: string;
  /** Breadcrumb items — when provided, renders breadcrumbs instead of title */
  breadcrumbs?: BreadcrumbItem[];
  /** Property name shown in the property switcher */
  propertyName?: string;
  /** Whether to show the property switcher button */
  showPropertySwitcher?: boolean;
  /** List of properties for the dropdown switcher */
  properties?: TopbarProperty[];
  /** Callback when a property is selected from the dropdown */
  onPropertySelect?: (property: TopbarProperty) => void;
  /** User initials for the avatar circle */
  userInitials?: string;
  /** User display name */
  userName?: string;
  /** User role label */
  userRole?: string;
  /** Notification count — if > 0, the red dot is shown */
  notificationCount?: number;
  /** Callback when the search input value changes */
  onSearch?: (query: string) => void;
  /** Callback when Cmd/Ctrl+K is pressed */
  onCommandK?: () => void;
  /** Callback when the notification bell is clicked */
  onNotificationsClick?: () => void;
  /** Callback when the property switcher is clicked */
  onPropertyClick?: () => void;
  /** Callback when the user avatar area is clicked */
  onUserClick?: () => void;
  /** Callback when "My Profile" is clicked in user menu */
  onProfileClick?: () => void;
  /** Callback when "Settings" is clicked in user menu */
  onSettingsClick?: () => void;
  /** Callback when "Sign Out" is clicked in user menu */
  onLogoutClick?: () => void;
  /** Callback when mobile hamburger menu is clicked */
  onMenuClick?: () => void;
  className?: string;
}

// ---------------------------------------------------------------------------
// Topbar
// ---------------------------------------------------------------------------
export function Topbar({
  title,
  breadcrumbs,
  propertyName = 'HotelStack Grand Delhi',
  showPropertySwitcher = true,
  properties = [],
  onPropertySelect,
  userInitials = 'AK',
  userName = 'Admin User',
  userRole = 'Hotel Admin',
  notificationCount = 0,
  onSearch,
  onCommandK,
  onNotificationsClick,
  onPropertyClick,
  onUserClick,
  onProfileClick,
  onSettingsClick,
  onLogoutClick,
  onMenuClick,
  className,
}: TopbarProps) {
  const { t, dir } = useTranslation();
  const searchRef = useRef<HTMLInputElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const propertyMenuRef = useRef<HTMLDivElement>(null);
  const [searchValue, setSearchValue] = useState('');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showPropertyMenu, setShowPropertyMenu] = useState(false);

  // -----------------------------------------------------------------------
  // Global Cmd/Ctrl + K shortcut to focus search
  // -----------------------------------------------------------------------
  const handleGlobalKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (onCommandK) {
          onCommandK();
        } else {
          searchRef.current?.focus();
        }
      }
    },
    [onCommandK]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [handleGlobalKeyDown]);

  // Close user menu on click outside
  useEffect(() => {
    if (!showUserMenu) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu]);

  // Close property menu on click outside
  useEffect(() => {
    if (!showPropertyMenu) return;
    const handleClickOutsideProp = (e: MouseEvent) => {
      if (propertyMenuRef.current && !propertyMenuRef.current.contains(e.target as Node)) {
        setShowPropertyMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutsideProp);
    return () => document.removeEventListener('mousedown', handleClickOutsideProp);
  }, [showPropertyMenu]);

  const handlePropertyToggle = useCallback(() => {
    if (properties.length > 0) {
      setShowPropertyMenu((prev) => !prev);
    } else {
      onPropertyClick?.();
    }
  }, [properties.length, onPropertyClick]);

  const handlePropertyItemClick = useCallback((prop: TopbarProperty) => {
    setShowPropertyMenu(false);
    onPropertySelect?.(prop);
  }, [onPropertySelect]);

  const handleUserMenuToggle = useCallback(() => {
    setShowUserMenu((prev) => !prev);
    onUserClick?.();
  }, [onUserClick]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    onSearch?.(e.target.value);
  };

  const hasNotifications = notificationCount > 0;

  return (
    <header
      id="topbar-header"
      className={cn(
        'h-16 flex items-center justify-between px-3 lg:px-6 sticky top-0 z-40',
        'bg-hms-bg/80 backdrop-blur-xl border-b border-hms-border/50',
        className
      )}
    >
      {/* ---------------------------------------------------------------- */}
      {/* Left — Mobile Menu + Breadcrumb / Title                           */}
      {/* ---------------------------------------------------------------- */}
      <div className="flex items-center gap-3 min-w-0">
        {/* Mobile hamburger — visible only below md */}
        {onMenuClick && (
          <button
            id="topbar-mobile-menu"
            onClick={onMenuClick}
            className={cn(
              'flex lg:hidden items-center justify-center w-10 h-10 -ms-2 rounded-lg',
              'text-hms-textSub hover:text-hms-text hover:bg-white/[0.05]',
              'transition-all duration-200'
            )}
            aria-label={t('topbar.openMenu')}
          >
            <Menu size={20} strokeWidth={1.8} />
          </button>
        )}
        {breadcrumbs && breadcrumbs.length > 0 ? (
          <Breadcrumb items={breadcrumbs} />
        ) : title ? (
          <h1 className="text-[15px] font-semibold text-hms-text tracking-tight truncate">
            {title}
          </h1>
        ) : null}
      </div>

      {/* ---------------------------------------------------------------- */}
      {/* Center — Search                                                   */}
      {/* ---------------------------------------------------------------- */}
      <div className="hidden lg:flex items-center justify-center flex-1 max-w-md mx-6">
        <div
          className={cn(
            'relative flex items-center w-full',
            'bg-white/[0.04] border border-white/[0.08] rounded-xl',
            'focus-within:border-hms-gold/30 focus-within:bg-white/[0.06]',
            'transition-all duration-200'
          )}
        >
          <Search
            className="absolute start-3.5 text-hms-textDim pointer-events-none"
            size={15}
            strokeWidth={2}
          />

          <input
            id="topbar-search-input"
            ref={searchRef}
            type="text"
            value={searchValue}
            onChange={handleSearchChange}
            // With a palette, the box is its launcher: a click (or Enter) opens
            // it, and typing happens there. Without either it is inert.
            onClick={!onSearch ? onCommandK : undefined}
            onKeyDown={!onSearch && onCommandK ? (e) => { if (e.key === 'Enter') onCommandK(); } : undefined}
            placeholder={t(onSearch || onCommandK ? 'shared.commandPalette.searchPlaceholder' : 'topbar.searchPlaceholder')}
            readOnly={!onSearch}
            aria-haspopup={!onSearch && onCommandK ? 'dialog' : undefined}
            className={cn(
              'w-full bg-transparent ps-10 pe-16 py-2.5',
              'text-[13px] font-medium text-hms-text placeholder:text-hms-textDim',
              'outline-none',
              'font-[inherit]',
              !onSearch && (onCommandK ? 'cursor-pointer' : 'cursor-default')
            )}
          />

          {/* Command-K badge */}
          <div className="absolute end-3 flex items-center gap-1 pointer-events-none">
            <kbd
              className={cn(
                'flex items-center gap-0.5 px-1.5 py-0.5 rounded-md',
                'bg-white/[0.06] border border-white/[0.08]',
                'text-[10px] font-medium text-hms-textDim'
              )}
            >
              <Command size={10} strokeWidth={2} />
              <span>K</span>
            </kbd>
          </div>
        </div>
      </div>

      {/* ---------------------------------------------------------------- */}
      {/* Right — Actions                                                   */}
      {/* ---------------------------------------------------------------- */}
      <div className="flex items-center gap-1.5">
        {/* Offline Indicator */}
        <OfflineIndicator id="topbar-offline-indicator" compact />

        {/* Language Selector */}
        <LanguageSelector id="topbar-language-selector" compact className="hidden sm:flex" />

        {/* Theme Toggle */}
        <ThemeToggle />

        {/* Notification Bell */}
          <button
            id="topbar-notification-bell"
            onClick={onNotificationsClick}
            className={cn(
              'relative flex items-center justify-center w-10 h-10 rounded-lg',
              'text-hms-textSub hover:text-hms-text hover:bg-white/[0.05]',
              'transition-all duration-200'
            )}
            aria-label={t('topbar.notifications')}
          >
            <Bell size={18} strokeWidth={1.8} />
            {hasNotifications && (
              <span id="topbar-notification-dot" className="absolute top-2 end-2 w-2 h-2 rounded-full bg-hms-red ring-2 ring-hms-bg" />
            )}
          </button>

        {/* Divider */}
        <div className="hidden sm:block w-px h-7 bg-hms-border/60 mx-1" />

        {/* Property Switcher */}
        {showPropertySwitcher && (
          <div ref={propertyMenuRef} className="relative">
            <button
              id="topbar-property-switcher"
              onClick={handlePropertyToggle}
              className={cn(
                'flex items-center gap-2.5 px-3 py-2 rounded-lg',
                'hover:bg-white/[0.05] transition-all duration-200',
                'max-w-[220px]',
                showPropertyMenu && 'bg-white/[0.05]'
              )}
            >
              <div className="w-7 h-7 rounded-lg bg-hms-teal/15 flex items-center justify-center flex-shrink-0">
                <Building2 className="text-hms-teal" size={14} strokeWidth={2} />
              </div>
              <span dir="auto" className="text-[12.5px] font-medium text-hms-textSub truncate hidden lg:inline">
                {propertyName}
              </span>
              <ChevronDown
                className={cn(
                  'text-hms-textDim flex-shrink-0 transition-transform duration-200',
                  showPropertyMenu && 'rotate-180'
                )}
                size={13}
                strokeWidth={2}
              />
            </button>

            {/* Property Dropdown */}
            {showPropertyMenu && properties.length > 0 && (
              <div
                id="topbar-property-menu"
                className={cn(
                  'absolute end-0 top-full mt-2 w-72 max-w-[calc(100vw-1rem)] z-50',
                  'bg-hms-card border border-hms-border rounded-xl shadow-2xl',
                  'py-1.5 animate-fadeIn'
                )}
              >
                <div className="px-4 py-2.5 border-b border-hms-border mb-1">
                  <p className="text-[11px] font-semibold text-hms-textDim uppercase tracking-wider">{t('topbar.switchHotel')}</p>
                </div>
                <div className="max-h-[280px] overflow-y-auto">
                  {properties.map((prop) => (
                    <button
                      key={prop.id}
                      id={`topbar-property-item-${prop.id}`}
                      onClick={() => handlePropertyItemClick(prop)}
                      className={cn(
                        'w-full flex items-center gap-3 px-4 py-2.5',
                        'hover:bg-white/[0.04] transition-colors'
                      )}
                    >
                      <div className="w-8 h-8 rounded-lg bg-hms-gold/10 flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-3.5 h-3.5 text-hms-gold" />
                      </div>
                      <div className="flex-1 min-w-0 text-start">
                        <p className="text-[12.5px] font-medium text-hms-text truncate">{prop.name}</p>
                        <div className="flex items-center gap-1 mt-0.5">
                          <MapPin className="w-2.5 h-2.5 text-hms-textDim flex-shrink-0" />
                          <span className="text-[10.5px] text-hms-textDim">{prop.city}</span>
                          {prop.status && (
                            <span className={cn(
                              'ms-1.5 text-[9px] font-medium px-1.5 py-0.5 rounded-full',
                              prop.status === 'Active' ? 'bg-hms-green/10 text-hms-green' : 'bg-hms-amber/10 text-hms-amber'
                            )}>
                              {prop.status}
                            </span>
                          )}
                        </div>
                      </div>
                      <ExternalLink className="w-3.5 h-3.5 text-hms-textDim flex-shrink-0" />
                    </button>
                  ))}
                </div>
                <div className="border-t border-hms-border mt-1 pt-1">
                  <button
                    id="topbar-property-view-all"
                    onClick={() => { setShowPropertyMenu(false); onPropertyClick?.(); }}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-[12px] font-medium text-hms-teal hover:bg-white/[0.04] transition-colors"
                  >
                    {t('topbar.viewAllProperties')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Divider */}
        <div className="hidden sm:block w-px h-7 bg-hms-border/60 mx-1" />

        {/* User Avatar + Menu */}
        <div ref={userMenuRef} className="relative">
          <button
            id="topbar-user-btn"
            onClick={handleUserMenuToggle}
            className={cn(
              'flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg',
              'hover:bg-white/[0.05] transition-all duration-200'
            )}
          >
            <div
              className={cn(
                'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
                'bg-gradient-to-br from-hms-gold/80 to-hms-goldLt/80',
                'text-hms-bg text-[11px] font-bold tracking-wide'
              )}
            >
              {userInitials}
            </div>

            <div className="hidden lg:block text-start min-w-0">
              <p className="text-[12.5px] font-medium text-hms-text leading-none truncate">
                {userName}
              </p>
              <p className="text-[10.5px] text-hms-textDim mt-0.5 leading-none truncate">
                {userRole}
              </p>
            </div>

            <ChevronDown
              id="topbar-user-chevron"
              className={cn(
                'text-hms-textDim flex-shrink-0 hidden lg:block transition-transform duration-200',
                showUserMenu && 'rotate-180'
              )}
              size={13}
              strokeWidth={2}
            />
          </button>

          {/* User Dropdown Menu */}
          {showUserMenu && (
            <div
              id="topbar-user-menu"
              className={cn(
                'absolute end-0 top-full mt-2 w-56 max-w-[calc(100vw-1rem)] z-50',
                'bg-hms-card border border-hms-border rounded-xl shadow-2xl',
                'py-1.5 animate-fadeIn'
              )}
            >
              <div className="px-4 py-2.5 border-b border-hms-border mb-1.5">
                <p className="text-[13px] font-medium text-hms-text truncate">{userName}</p>
                <p className="text-[11px] text-hms-textDim truncate">{userRole}</p>
              </div>
              <button
                id="topbar-user-profile"
                onClick={() => { onProfileClick?.(); setShowUserMenu(false); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-[13px] text-hms-textSub hover:text-hms-text hover:bg-white/[0.04] transition-colors"
              >
                <User size={15} strokeWidth={1.8} />
                {t('topbar.myProfile')}
              </button>
              <button
                id="topbar-user-settings"
                onClick={() => { onSettingsClick?.(); setShowUserMenu(false); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-[13px] text-hms-textSub hover:text-hms-text hover:bg-white/[0.04] transition-colors"
              >
                <SettingsIcon size={15} strokeWidth={1.8} />
                {t('topbar.settings')}
              </button>
              <div className="h-px bg-hms-border mx-3 my-1.5" />
              <button
                id="topbar-user-logout"
                onClick={() => { onLogoutClick?.(); setShowUserMenu(false); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-[13px] text-hms-red hover:bg-hms-red/5 transition-colors"
              >
                <LogOut size={15} strokeWidth={1.8} />
                {t('topbar.signOut')}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
